import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Save, ArrowLeft, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import LayoutEditor from "./LayoutEditor";

export function SEOPagesList() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/admin/seo-pages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/admin/seo-pages", {
      slug: `new-page-${Date.now()}`, h1: "New Landing Page",
      meta_title: "", meta_description: "", keywords: "", og_image: "",
      hero_image: "", content_sections: [], faqs: [], cta_text: "Enquire now",
      schema_json: "", is_published: false, layout: [],
    });
    nav(`/admin/seo-pages/${data.id}`);
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/seo-pages/${id}`);
    load();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/seo-pages/${id}/duplicate`);
      toast.success("Duplicated as draft");
      nav(`/admin/seo-pages/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not duplicate");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">SEO landing pages</div>
          <h1 className="font-display text-4xl mt-3">SEO pages</h1>
        </div>
        <button onClick={create} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid="new-seo-page"><Plus className="w-4 h-4" /> New page</button>
      </div>

      <div className="mt-8 bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr><th className="p-3 text-left">H1</th><th className="p-3 text-left">Slug</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100" data-testid={`seo-row-${p.slug}`}>
                <td className="p-3 font-medium">{p.h1}</td>
                <td className="p-3 text-zinc-500">/{p.slug}</td>
                <td className="p-3"><span className={`text-[10px] px-2 py-1 rounded-full ${p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{p.is_published ? "Published" : "Draft"}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Link to={`/admin/seo-pages/${p.id}`} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md mr-2 hover:bg-zinc-50">Edit</Link>
                  <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md inline-flex items-center gap-1 mr-2 hover:bg-zinc-50"><ExternalLink className="w-3 h-3" /></a>
                  <button onClick={() => duplicate(p.id)} title="Duplicate" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md mr-2 hover:bg-zinc-50 inline-flex items-center gap-1" data-testid={`seo-duplicate-${p.slug}`}><Copy className="w-3 h-3" /></button>
                  <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SEOPageEdit() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/admin/seo-pages/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <div>Loading…</div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/seo-pages/${id}`, p); setP(data); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addSec = () => set("content_sections", [...(p.content_sections || []), { title: "", body: "" }]);
  const addFaq = () => set("faqs", [...(p.faqs || []), { q: "", a: "" }]);

  return (
    <div>
      <Link to="/admin/seo-pages" className="text-xs text-zinc-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All pages</Link>
      <div className="flex items-end justify-between mt-3 flex-wrap gap-4">
        <h1 className="font-display text-3xl">{p.h1}</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="seo-published" /> Published</label>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md" data-testid="seo-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <Tabs defaultValue="basics" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="basics" data-testid="seo-tab-basics">Hero &amp; text</TabsTrigger>
          <TabsTrigger value="sections" data-testid="seo-tab-sections">Sections (drag &amp; drop)</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="seo-tab-faqs">FAQs</TabsTrigger>
          <TabsTrigger value="seo" data-testid="seo-tab-seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Input value={p.h1 || ""} onChange={(e) => set("h1", e.target.value)} placeholder="H1" />
              <Input value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="Slug" />
              <ImageUploader label="Hero image" value={p.hero_image} onChange={(v) => set("hero_image", v)} testId="seo-hero" />

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3"><div className="text-sm font-medium">Content sections (simple)</div><button onClick={addSec} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add</button></div>
                <p className="text-xs text-zinc-500 mb-3">Quick title + body sections. For richer layouts, use the <b>Sections</b> tab.</p>
                <div className="space-y-3">
                  {(p.content_sections || []).map((s, i) => (
                    <div key={i} className="bg-zinc-50 border rounded-md p-3 space-y-2">
                      <Input value={s.title} onChange={(e) => { const arr = [...p.content_sections]; arr[i] = { ...arr[i], title: e.target.value }; set("content_sections", arr); }} placeholder="Title" />
                      <Textarea value={s.body} onChange={(e) => { const arr = [...p.content_sections]; arr[i] = { ...arr[i], body: e.target.value }; set("content_sections", arr); }} placeholder="Body" rows={4} />
                      <button onClick={() => set("content_sections", p.content_sections.filter((_, idx) => idx !== i))} className="text-xs text-red-600">Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <Input value={p.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} placeholder="CTA button text" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sections" className="mt-6">
          <div className="bg-white border border-zinc-200 rounded-md p-4 md:p-6">
            <p className="text-sm text-zinc-500 mb-4">Build the page with the same drag‑and‑drop section builder used on Properties. These sections render <b>after</b> the hero and above the FAQs.</p>
            <LayoutEditor layout={p.layout || []} onChange={(next) => set("layout", next)} coreEnabled={false} />
          </div>
        </TabsContent>

        <TabsContent value="faqs" className="mt-6">
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3"><div className="text-sm font-medium">FAQs</div><button onClick={addFaq} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add</button></div>
            <div className="space-y-3">
              {(p.faqs || []).map((f, i) => (
                <div key={i} className="bg-zinc-50 border rounded-md p-3 space-y-2">
                  <Input value={f.q} onChange={(e) => { const arr = [...p.faqs]; arr[i] = { ...arr[i], q: e.target.value }; set("faqs", arr); }} placeholder="Question" />
                  <Textarea value={f.a} onChange={(e) => { const arr = [...p.faqs]; arr[i] = { ...arr[i], a: e.target.value }; set("faqs", arr); }} placeholder="Answer" rows={3} />
                  <button onClick={() => set("faqs", p.faqs.filter((_, idx) => idx !== i))} className="text-xs text-red-600">Remove</button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-6 space-y-3 max-w-2xl">
          <Input value={p.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} placeholder="Meta title" />
          <Textarea value={p.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} placeholder="Meta description" rows={3} />
          <Input value={p.keywords || ""} onChange={(e) => set("keywords", e.target.value)} placeholder="Keywords" />
          <ImageUploader imageOnly label="OG image" value={p.og_image} onChange={(v) => set("og_image", v)} testId="seo-og" />
          <Textarea value={p.schema_json || ""} onChange={(e) => set("schema_json", e.target.value)} placeholder="Custom schema JSON (optional)" rows={4} className="font-mono text-xs" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
